import i18next from 'i18next';

// SVG text has no layout engine, so charts reserve axis room from the widest formatted label
// instead of a fixed guess (a fixed 44px clipped "10,000,000.00").
const AVG_CHAR_EM = 0.58; // average glyph width of the dashboard font, in em (digits/commas)

export function textWidth(text: string, fontSize: number): number {
    return Math.ceil(text.length * fontSize * AVG_CHAR_EM);
}

/** Left padding for a y-axis: room for the widest tick label, never below `min`. */
export function axisPadLeft(labels: string[], fontSize: number, min: number): number {
    const widest = labels.reduce((w, l) => Math.max(w, textWidth(l, fontSize)), 0);
    return Math.max(min, widest + 10);
}

/** Right padding for value labels drawn after a bar, never below `min`. */
export function labelPadRight(labels: string[], fontSize: number, min: number): number {
    const widest = labels.reduce((w, l) => Math.max(w, textWidth(l, fontSize)), 0);
    return Math.max(min, widest + 10);
}

/** Compact money for axis ticks: 1,500,000 -> 1.5M, 750,000 -> 750K, 900 -> 900. Tooltips stay exact. */
export function compactMoney(n: number): string {
    const abs = Math.abs(n);
    const trim = (v: number) => String(Math.round(v * 10) / 10);
    if (abs >= 1e9) return `${trim(n / 1e9)}B`;
    if (abs >= 1e6) return `${trim(n / 1e6)}M`;
    if (abs >= 1e3) return `${trim(n / 1e3)}K`;
    return String(Math.round(n * 10) / 10);
}

/**
 * 'YYYY-MM' -> 'Mar 2026' ('Mar 26' when `short`), in the current language with Latin digits
 * ('মার্চ 2026' in Bangla). Anything else is returned unchanged.
 */
export function formatPeriodLabel(label: string, short = false, language: string = i18next.language || 'en'): string {
    const m = /^(\d{4})-(\d{2})$/.exec(label);
    if (!m) return label;
    const month = Number(m[2]);
    if (month < 1 || month > 12) return label;
    // The app's own short month names (common:monthsShort), so charts match every other screen
    // (Intl's en-GB gives 'Sept'). Years keep Latin digits.
    const name = i18next.t(`monthsShort.${month}`, { ns: 'common', lng: language });
    return `${name} ${short ? m[1].slice(2) : m[1]}`;
}

/** Show every Nth category label so the widest label never collides with its neighbour. */
export function labelEvery(labels: string[], step: number, fontSize: number): number {
    const widest = labels.reduce((w, l) => Math.max(w, textWidth(l, fontSize)), 0);
    return Math.max(1, Math.ceil((widest + 8) / Math.max(1, step)));
}
